import { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, Building2, MapPin, X, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export interface KVKCompany {
  kvkNumber: string;
  name: string;
  tradeName?: string;
  street?: string;
  houseNumber?: string;
  huisnummerToevoeging?: string;
  postalCode?: string;
  city?: string;
  website?: string;
  isActive: boolean;
}

interface CompanyLookupInputProps {
  onSelect: (company: KVKCompany) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  initialValue?: string;
}

export const CompanyLookupInput = ({
  onSelect,
  placeholder = 'Zoek op bedrijfsnaam (min. 3 tekens)...',
  className,
  disabled = false,
  initialValue = '',
}: CompanyLookupInputProps) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState<KVKCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [remainingCalls, setRemainingCalls] = useState<number | null>(null);
  const [rateLimitError, setRateLimitError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const searchCompanies = useCallback(async (searchQuery: string) => {
    // Min 3 characters requirement
    if (searchQuery.length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);
    setRateLimitError(false);
    
    try {
      const { data, error } = await supabase.functions.invoke('search-company', {
        body: { 
          action: 'search',
          query: searchQuery,
        },
      });

      if (error) throw error;
      
      // Check for rate limit error
      if (data?.error && data?.remainingCalls === 0) {
        setRateLimitError(true);
        toast({
          title: "Limiet bereikt",
          description: data.error,
          variant: "destructive",
        });
        setResults([]);
        return;
      }

      // Update remaining calls
      if (typeof data?.remainingCalls === 'number') {
        setRemainingCalls(data.remainingCalls);
      }

      // Transform results to KVKCompany format
      const companies: KVKCompany[] = (data?.results || []).map((item: any) => ({
        kvkNumber: item.kvkNummer || '',
        name: item.naam || '',
        tradeName: item.handelsnaam,
        street: item.straatnaam,
        houseNumber: item.huisnummer,
        postalCode: item.postcode,
        city: item.plaats,
        isActive: true,
      }));

      setResults(companies);
      setShowDropdown(true);
      setSelectedIndex(-1);
    } catch (error: any) {
      console.error('Error searching companies:', error);
      if (error?.message?.includes('429') || error?.message?.includes('limiet')) {
        setRateLimitError(true);
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch full details when a company is selected
  const fetchCompanyDetails = useCallback(async (kvkNumber: string, basicCompany: KVKCompany) => {
    try {
      const { data, error } = await supabase.functions.invoke('search-company', {
        body: { 
          action: 'get-details',
          query: kvkNumber,
        },
      });

      if (error) throw error;

      // Update remaining calls
      if (typeof data?.remainingCalls === 'number') {
        setRemainingCalls(data.remainingCalls);
      }

      // Return enriched company data
      return {
        kvkNumber: data.kvkNummer || kvkNumber,
        name: data.naam || basicCompany.name,
        tradeName: data.handelsnaam || basicCompany.tradeName,
        street: data.straatnaam || basicCompany.street,
        houseNumber: data.huisnummer ? 
          (data.huisnummerToevoeging ? `${data.huisnummer}${data.huisnummerToevoeging}` : data.huisnummer) 
          : basicCompany.houseNumber,
        postalCode: data.postcode || basicCompany.postalCode,
        city: data.plaats || basicCompany.city,
        website: data.website,
        isActive: true,
      } as KVKCompany;
    } catch (error) {
      console.error('Error fetching company details:', error);
      // Return basic company if details fetch fails
      return basicCompany;
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce search with 500ms delay
    debounceRef.current = setTimeout(() => {
      searchCompanies(value);
    }, 500);
  };

  const handleSelect = async (company: KVKCompany) => {
    setQuery(company.name);
    setShowDropdown(false);
    setResults([]);
    
    // Fetch full details to get complete address
    setLoading(true);
    const detailedCompany = await fetchCompanyDetails(company.kvkNumber, company);
    setLoading(false);
    
    onSelect(detailedCompany);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled || rateLimitError}
          className={cn("pl-9 pr-16", rateLimitError && "border-destructive")}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {rateLimitError && <AlertCircle className="h-4 w-4 text-destructive" />}
          {query && !loading && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Remaining calls indicator */}
      {remainingCalls !== null && remainingCalls <= 5 && !rateLimitError && (
        <p className="text-xs text-muted-foreground mt-1">
          Nog {remainingCalls} zoekopdracht{remainingCalls !== 1 ? 'en' : ''} vandaag
        </p>
      )}

      {/* Dropdown */}
      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-64 overflow-auto"
        >
          {results.map((company, index) => (
            <button
              key={`${company.kvkNumber}-${index}`}
              type="button"
              className={cn(
                'w-full px-3 py-2 text-left hover:bg-accent transition-colors',
                selectedIndex === index && 'bg-accent',
                !company.isActive && 'opacity-60'
              )}
              onClick={() => handleSelect(company)}
            >
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">
                    {company.name}
                    {!company.isActive && (
                      <span className="ml-2 text-xs text-muted-foreground">(inactief)</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                    <span>KVK: {company.kvkNumber}</span>
                    {company.city && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {company.city}
                        </span>
                      </>
                    )}
                  </div>
                  {company.street && (
                    <div className="text-xs text-muted-foreground truncate">
                      {company.street} {company.houseNumber}
                      {company.postalCode && `, ${company.postalCode}`}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showDropdown && query.length >= 3 && !loading && results.length === 0 && !rateLimitError && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg p-3 text-sm text-muted-foreground text-center">
          Geen bedrijven gevonden voor "{query}"
        </div>
      )}

      {/* Rate limit message */}
      {rateLimitError && (
        <div className="absolute z-50 w-full mt-1 bg-destructive/10 border border-destructive rounded-md shadow-lg p-3 text-sm text-destructive text-center">
          Dagelijks limiet bereikt. Probeer het morgen opnieuw.
        </div>
      )}
    </div>
  );
};
